import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { track } from "@utils/appUtils";
import { useCallback } from "react";

/**
 * Centralized analytics tracking for the Discover feature.
 *
 * This hook provides typed tracking functions for all Discover-related events.
 * All events follow the Amplitude Instrumentation Standards:
 * - Use SegmentEventName.CLICK or .VIEW
 * - Properties in snake_case
 * - UI components in Title Case
 *
 * @see https://www.notion.so/halodotscience/Amplitude-Instrumentation-Standards-27a66fd0c3fb4972a276e85a5073ca46
 * @see https://www.notion.so/halodotscience/1-0-Discover-feature-MVP-beta-release-to-Kraft-Q3-spillover-23136397a78b80aea78dc07039b72dbc
 *
 * @example
 * ```tsx
 * const { trackLearnMoreClick } = useDiscoverAnalytics();
 *
 * const handleClick = () => {
 *   trackLearnMoreClick({
 *     solution_listing_id: solution.id,
 *     listing_position: 0,
 *   });
 * };
 * ```
 */
export function useDiscoverAnalytics() {
  /**
   * Event 1: Track when user clicks Message button on solution listing
   *
   * @param params.listing_position - zero if from solution listing; or position of the solution in the feed
   * @param params.solution_listing_id - ID of the solution listing
   */
  const trackMessageClick = useCallback(
    (params: { listing_position: number; solution_listing_id: string }) => {
      track(SegmentEventName.CLICK, {
        ui_component: "Partnership Listing Message",
        ...params,
      });
    },
    []
  );

  /**
   * Event 2: Track when user clicks Learn More button
   *
   * @param params.solution_listing_id - ID of the solution listing
   * @param params.listing_position - Zero-indexed position of the solution in the feed
   */
  const trackLearnMoreClick = useCallback(
    (params: { solution_listing_id: string; listing_position: number }) => {
      track(SegmentEventName.CLICK, {
        ui_component: "Partnership Listing Learn More",
        ...params,
      });
    },
    []
  );

  /**
   * Event 3: Track when user opens the personalization banner
   *
   */
  const trackPersonalizeFeedClick = useCallback(() => {
    track(SegmentEventName.CLICK, {
      ui_component: "Discover Personalize Feed",
    });
  }, []);

  /**
   * Event 4: Track when user submits personalization data
   *
   * @param params.personalization_content - The personalization data provided by the user
   */
  const trackPersonalizationSubmitted = useCallback(
    (params: { personalization_content: Record<string, any> | string }) => {
      track(SegmentEventName.CLICK, {
        ui_component: "Discover Personalization Submitted",
        ...params,
      });
    },
    []
  );

  /**
   * Event 5: Track when user clicks Discover tab in navigation
   *
   * @param params.current_page - Page user is navigating FROM
   */
  const trackDiscoverNavClick = useCallback((params: { current_page: string }) => {
    track(SegmentEventName.CLICK, {
      ui_component: "Discover Nav Click",
      ...params,
    });
  }, []);

  /**
   * Event 6: Track Discover feed page view
   *
   * @param params.is_personalized_feed - Whether user has personalized their feed
   * @param params.referrer - URL/source that led to Discover page
   */
  const trackPartnershipListingsFeedView = useCallback(
    (params: { is_personalized_feed: boolean; referrer: string }) => {
      track(SegmentEventName.VIEW, {
        ui_component: "Discover Partnership Listings Feed View",
        react_component: "DiscoverPage",
        ...params,
      });
    },
    []
  );

  /**
   * Event 7: Track For You feed page view
   *
   * @param params.is_personalized_feed - Whether user has personalized their feed
   * @param params.referrer - URL/source that led to Discover page
   */
  const trackForYouFeedView = useCallback(
    (params: { is_personalized_feed: boolean; referrer: string }) => {
      track(SegmentEventName.VIEW, {
        ui_component: "Discover For You Feed View",
        react_component: "DiscoverPage",
        ...params,
      });
    },
    []
  );

  /**
   * Event 8: Track when user shares a solution listing
   *
   * @param params.solution_listing_id - ID of the solution being shared
   */
  const trackShareListing = useCallback((params: { solution_listing_id: string }) => {
    track(SegmentEventName.CLICK, {
      ui_component: "Share Listing",
      ...params,
    });
  }, []);

  /**
   * Event 9: Track when user requests intro (when no direct contact available)
   *
   * @param params.listing_position - Zero-indexed position of the solution in the feed
   * @param params.solution_listing_id - ID of the solution listing
   */
  const trackRequestIntro = useCallback(
    (params: { listing_position: number; solution_listing_id: string }) => {
      track(SegmentEventName.CLICK, {
        ui_component: "Request Intro",
        ...params,
      });
    },
    []
  );

  /**
   * Event 10: Track when user reports an error in solution listing
   *
   * @param params.solution_listing_id - ID of the solution listing being reported
   */
  const trackReportError = useCallback((params: { solution_listing_id: string }) => {
    track(SegmentEventName.CLICK, {
      ui_component: "Report Error",
      ...params,
    });
  }, []);

  /**
   * Event 11: Track when user submits request intro form
   *
   * @param params.solution_listing_id - ID of the solution listing
   * @param params.message_length - Length of the message provided
   */
  const trackRequestIntroSubmitted = useCallback(
    (params: { solution_listing_id: string; message_length: number }) => {
      track(SegmentEventName.SUBMIT, {
        ui_component: "Request Intro Submitted",
        ...params,
      });
    },
    []
  );

  /**
   * Event 12: Track when user clicks Share button on innovator card
   *
   * @param params.innovator_id - ID of the innovator
   * @param params.innovator_position - Zero-indexed position of the innovator in the feed
   */
  const trackInnovatorShare = useCallback(
    (params: { innovator_id: number; innovator_position: number }) => {
      track(SegmentEventName.CLICK, {
        ui_component: "Discover Innovator Share",
        ...params,
      });
    },
    []
  );

  /**
   * Event 13: Track when user clicks Add to network button on innovator card
   *
   * @param params.innovator_id - ID of the innovator
   * @param params.innovator_position - Zero-indexed position of the innovator in the feed
   */
  const trackInnovatorAddToNetwork = useCallback(
    (params: { innovator_id: number; innovator_position: number }) => {
      track(SegmentEventName.CLICK, {
        ui_component: "Discover Innovator Add to Network",
        ...params,
      });
    },
    []
  );

  /**
   * Event 14: Track when user clicks Message button on innovator card
   *
   * @param params.innovator_id - ID of the innovator
   * @param params.innovator_position - Zero-indexed position of the innovator in the feed
   */
  const trackInnovatorMessage = useCallback(
    (params: { innovator_id: number; innovator_position: number }) => {
      track(SegmentEventName.CLICK, {
        ui_component: "Discover Innovator Message",
        ...params,
      });
    },
    []
  );

  /**
   * Event 15: Track when user clicks Learn More button on innovator card
   *
   * @param params.innovator_id - ID of the innovator
   * @param params.innovator_position - Zero-indexed position of the innovator in the feed
   */
  const trackInnovatorLearnMore = useCallback(
    (params: { innovator_id: number; innovator_position: number }) => {
      track(SegmentEventName.CLICK, {
        ui_component: "Discover Innovator Learn More",
        ...params,
      });
    },
    []
  );

  return {
    trackMessageClick,
    trackLearnMoreClick,
    trackPersonalizeFeedClick,
    trackPersonalizationSubmitted,
    trackDiscoverNavClick,
    trackPartnershipListingsFeedView,
    trackForYouFeedView,
    trackShareListing,
    trackRequestIntro,
    trackReportError,
    trackRequestIntroSubmitted,
    trackInnovatorShare,
    trackInnovatorAddToNetwork,
    trackInnovatorMessage,
    trackInnovatorLearnMore,
  };
}
